
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

interface Order {
  id: string;
  customer_name: string;
  email: string;
  package_type: string;
  status: string;
  created_at: string;
  total_amount: number;
  property_address: string;
  phone: string;
  special_requests?: string;
  delivery_date?: string;
  photo_count?: number;
  approval_status?: string;
}

interface PhotoDelivery {
  id: string;
  order_id: string;
  photo_urls: string[];
  delivery_date: string;
  status: string;
  download_count: number;
  approval_status: string;
  client_feedback?: string;
}

const ClientPortalPage = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [photoDeliveries, setPhotoDeliveries] = useState<PhotoDelivery[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [searchEmail, setSearchEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('orders');
  const [feedback, setFeedback] = useState('');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState<PhotoDelivery | null>(null);

  const searchOrders = async () => {
    if (!searchEmail) return;
    
    setLoading(true);
    try {
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select('*')
        .eq('email', searchEmail.toLowerCase())
        .order('created_at', { ascending: false });

      if (ordersError) throw ordersError;
      setOrders(ordersData || []);

      if (ordersData && ordersData.length > 0) {
        const orderIds = ordersData.map(order => order.id);
        const { data: deliveriesData, error: deliveriesError } = await supabase
          .from('photo_deliveries')
          .select('*')
          .in('order_id', orderIds)
          .order('delivery_date', { ascending: false });

        if (deliveriesError) throw deliveriesError;
        setPhotoDeliveries(deliveriesData || []);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadPhotos = async (delivery: PhotoDelivery) => {
    try {
      // Increment download count
      await supabase
        .from('photo_deliveries')
        .update({ download_count: delivery.download_count + 1 })
        .eq('id', delivery.id);

      // Create download links for each photo
      delivery.photo_urls.forEach((url, index) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = `photo-${index + 1}.jpg`;
        link.click();
      });

      // Refresh data
      searchOrders();
    } catch (error) {
      console.error('Error downloading photos:', error);
    }
  };

  const submitFeedback = async () => {
    if (!selectedDelivery || !feedback.trim()) return;

    try {
      await supabase
        .from('photo_deliveries')
        .update({ 
          client_feedback: feedback,
          approval_status: 'feedback_provided'
        })
        .eq('id', selectedDelivery.id);

      setShowFeedbackModal(false);
      setFeedback('');
      setSelectedDelivery(null);
      searchOrders();
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const approvePhotos = async (deliveryId: string) => {
    try {
      await supabase
        .from('photo_deliveries')
        .update({ approval_status: 'approved' })
        .eq('id', deliveryId);

      searchOrders();
    } catch (error) {
      console.error('Error approving photos:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getApprovalColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'feedback_provided': return 'bg-blue-100 text-blue-800';
      case 'revision_requested': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Client Portal</h1>
            <p className="text-xl text-gray-600">Track your photography projects and download your photos</p>
          </div>

          {/* Search Section */}
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Access Your Projects</h2>
            <div className="flex gap-4">
              <input
                type="email"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                placeholder="Enter your email address"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                onKeyPress={(e) => e.key === 'Enter' && searchOrders()}
              />
              <button
                onClick={searchOrders}
                disabled={loading || !searchEmail}
                className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {loading ? 'Searching...' : 'Search Projects'}
              </button>
            </div>
          </div>

          {orders.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg">
              {/* Tabs */}
              <div className="border-b border-gray-200">
                <nav className="flex space-x-8 px-8 pt-6">
                  <button
                    onClick={() => setActiveTab('orders')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                      activeTab === 'orders'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Project Overview ({orders.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('photos')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                      activeTab === 'photos'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Photo Deliveries ({photoDeliveries.length})
                  </button>
                </nav>
              </div>

              <div className="p-8">
                {activeTab === 'orders' && (
                  <div className="space-y-6">
                    <h3 className="text-xl font-bold text-gray-900">Your Photography Projects</h3>
                    <div className="grid gap-6">
                      {orders.map((order) => (
                        <div key={order.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h4 className="text-lg font-semibold text-gray-900">{order.package_type}</h4>
                              <p className="text-gray-600">{order.property_address}</p>
                            </div>
                            <div className="text-right">
                              <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                                {order.status.replace('_', ' ').toUpperCase()}
                              </span>
                              <p className="text-lg font-bold text-gray-900 mt-2">${order.total_amount}</p>
                            </div>
                          </div>
                          
                          <div className="grid md:grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="font-medium text-gray-700">Order Date:</span>
                              <p className="text-gray-600">{new Date(order.created_at).toLocaleDateString()}</p>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Expected Delivery:</span>
                              <p className="text-gray-600">
                                {order.delivery_date ? new Date(order.delivery_date).toLocaleDateString() : 'TBD'}
                              </p>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Photo Count:</span>
                              <p className="text-gray-600">{order.photo_count || 'TBD'} images</p>
                            </div>
                          </div>
                          
                          {order.special_requests && (
                            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                              <span className="font-medium text-gray-700">Special Requests:</span>
                              <p className="text-gray-600 mt-1">{order.special_requests}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'photos' && (
                  <div className="space-y-6">
                    <h3 className="text-xl font-bold text-gray-900">Photo Deliveries</h3>
                    {photoDeliveries.length > 0 ? (
                      <div className="grid gap-6">
                        {photoDeliveries.map((delivery) => {
                          const relatedOrder = orders.find(order => order.id === delivery.order_id);
                          return (
                            <div key={delivery.id} className="border border-gray-200 rounded-lg p-6">
                              <div className="flex justify-between items-start mb-4">
                                <div>
                                  <h4 className="text-lg font-semibold text-gray-900">
                                    {relatedOrder?.package_type || 'Photography Package'}
                                  </h4>
                                  <p className="text-gray-600">{relatedOrder?.property_address}</p>
                                </div>
                                <div className="text-right">
                                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getApprovalColor(delivery.approval_status)}`}>
                                    {delivery.approval_status.replace('_', ' ').toUpperCase()}
                                  </span>
                                </div>
                              </div>
                              
                              <div className="grid md:grid-cols-4 gap-4 text-sm mb-4">
                                <div>
                                  <span className="font-medium text-gray-700">Delivered:</span>
                                  <p className="text-gray-600">{new Date(delivery.delivery_date).toLocaleDateString()}</p>
                                </div>
                                <div>
                                  <span className="font-medium text-gray-700">Photos:</span>
                                  <p className="text-gray-600">{delivery.photo_urls.length} images</p>
                                </div>
                                <div>
                                  <span className="font-medium text-gray-700">Downloads:</span>
                                  <p className="text-gray-600">{delivery.download_count} times</p>
                                </div>
                                <div>
                                  <span className="font-medium text-gray-700">Status:</span>
                                  <p className="text-gray-600">{delivery.status}</p>
                                </div>
                              </div>
                              
                              {delivery.client_feedback && (
                                <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                                  <span className="font-medium text-gray-700">Your Feedback:</span>
                                  <p className="text-gray-600 mt-1">{delivery.client_feedback}</p>
                                </div>
                              )}
                              
                              <div className="flex flex-wrap gap-3">
                                <button
                                  onClick={() => downloadPhotos(delivery)}
                                  className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap"
                                >
                                  <i className="ri-download-line mr-2"></i>
                                  Download Photos
                                </button>
                                
                                {delivery.approval_status === 'pending' && (
                                  <>
                                    <button
                                      onClick={() => approvePhotos(delivery.id)}
                                      className="bg-green-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors whitespace-nowrap"
                                    >
                                      <i className="ri-check-line mr-2"></i>
                                      Approve Photos
                                    </button>
                                    <button
                                      onClick={() => {
                                        setSelectedDelivery(delivery);
                                        setShowFeedbackModal(true);
                                      }}
                                      className="bg-orange-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-orange-700 transition-colors whitespace-nowrap"
                                    >
                                      <i className="ri-message-line mr-2"></i>
                                      Request Changes
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <i className="ri-image-line text-6xl text-gray-400 mb-4"></i>
                        <p className="text-gray-500">No photo deliveries yet. Your photos will appear here once ready.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {orders.length === 0 && searchEmail && !loading && (
            <div className="bg-white rounded-lg shadow-lg p-12 text-center">
              <i className="ri-search-line text-6xl text-gray-400 mb-4"></i>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Projects Found</h3>
              <p className="text-gray-600">
                We couldn't find any projects associated with this email address. 
                Please check your email or contact us for assistance.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Request Changes</h3>
            <p className="text-gray-600 mb-4">
              Please describe what changes you'd like to see in your photos:
            </p>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              placeholder="Describe the changes you'd like..."
              maxLength={500}
            ></textarea>
            <p className="text-xs text-gray-500 mb-4">Max 500 characters</p>
            <div className="flex gap-3">
              <button
                onClick={submitFeedback}
                disabled={!feedback.trim()}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                Submit Feedback
              </button>
              <button
                onClick={() => {
                  setShowFeedbackModal(false);
                  setFeedback('');
                  setSelectedDelivery(null);
                }}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg font-semibold hover:bg-gray-400 whitespace-nowrap"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ClientPortalPage;
